import { computed } from 'vue';

import {
  useApiKeysStore,
  useOauthApplicationStore,
  useWebhooksStore,
} from '@/stores';

import ApiFiltersTab from './tabs/ApiFiltersTab.vue';
import ApiIntegrationsTab from './tabs/ApiIntegrationsTab.vue';
import ApiKeysTab from './tabs/ApiKeysTab.vue';
import ApiWebhooksTab from './tabs/ApiWebhooksTab.vue';
import type { ApiTabType, ApiTabValueType } from './types';
import useApiFilterCreateGate from './useApiFilterCreateGate';
import useApiKeyCreateGate from './useApiKeyCreateGate';
import useApiTabAccess from './useApiTabAccess';

type ApiTabDetails = Omit<ApiTabType, 'value'>;

export default function useApiTabs() {
  const webhooksStore = useWebhooksStore();
  const oAuthApplicationStore = useOauthApplicationStore();
  const apiKeysStore = useApiKeysStore();
  const { createDisabledReason: createKeyDisabledReason } =
    useApiKeyCreateGate();
  const { filterCount, createDisabledReason: createFilterDisabledReason } =
    useApiFilterCreateGate();
  const { accessibleTabs } = useApiTabAccess();

  const tabsByValue = computed<Record<ApiTabValueType, ApiTabDetails>>(() => ({
    webhooks: {
      name: 'Webhooks',
      component: ApiWebhooksTab,
      count: webhooksStore.webhooks.length,
      createCtaText: 'Create webhook',
    },
    integrations: {
      name: 'API Integrations',
      component: ApiIntegrationsTab,
      count: oAuthApplicationStore.oAuthApplications.length,
      createCtaText: 'Create integration',
    },
    filters: {
      name: 'API Filters',
      component: ApiFiltersTab,
      count: filterCount.value,
      createCtaText: 'Create filter',
      createDisabledReason: createFilterDisabledReason.value,
    },
    keys: {
      name: 'API Keys',
      component: ApiKeysTab,
      count: apiKeysStore.apiKeys.length,
      createCtaText: 'Create API key',
      createDisabledReason: createKeyDisabledReason.value,
    },
  }));

  const tabs = computed(() =>
    accessibleTabs.value.map((value) => ({
      value,
      ...tabsByValue.value[value],
    })),
  );

  function getTabByValue(value: string | undefined) {
    return tabs.value.find((tab) => tab.value === value);
  }

  return { tabs, getTabByValue };
}
